import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import IconButton from '~components/icon-button';
import { BasicClose, TickSmallCorrect } from '~svg-components';
import { useToastContext } from './toast-context';
import ToastIcon from './toast-icon';

const variantIcon = {
  success: TickSmallCorrect,
  warning: (props) => <ToastIcon color="#fb8f23" {...props} />,
  error: (props) => <ToastIcon color="#d85145" {...props} />,
  info: (props) => <ToastIcon color="#0c8be6" isInfo {...props} />,
};

const useSnackItemStyles = makeStyles((theme) => ({
  root: {
    minWidth: 240,
    padding: '16px 40px 16px 16px',
    position: 'relative',
    backgroundColor: 'white',
    borderRadius: theme.borderRadius.small,
    boxShadow: theme.shadow.componentDark,
    '&::before': {
      width: 4,
      height: '100%',
      content: "''",
      position: 'absolute',
      top: 0,
      left: 0,
      borderTopLeftRadius: theme.borderRadius.small,
      borderBottomLeftRadius: theme.borderRadius.small,
    },
  },
  success: {
    '&::before': {
      backgroundColor: theme.colors.green,
    },
  },
  error: {
    '&::before': {
      backgroundColor: theme.colors.red,
    },
  },
  info: {
    '&::before': {
      backgroundColor: theme.colors.blue,
    },
  },
  warning: {
    '&::before': {
      backgroundColor: '#fb8f23',
    },
  },
  icon: {
    width: 16,
    minWidth: 16,
    height: 16,
    minHeight: 16,
    marginTop: 4,
  },
  iconVariant: {
    opacity: 0.9,
    marginRight: theme.spacing(1),
  },
  message: {
    display: 'flex',
    alignItems: 'flex-start',
    color: theme.colors.dark,
    fontSize: 14,
    lineHeight: '24px',
    wordBreak: 'unset',
  },
  iconButton: {
    padding: 6,
    border: 'none',
    position: 'absolute',
    top: 8,
    right: 8,
    transition: '.3s',
    '& svg': {
      width: 12,
      height: 12,
      fill: theme.colors.grey66,
    },
    '&:hover': {
      backgroundColor: 'white',
      '& svg': {
        fill: theme.colors.brandIcon,
      },
    },
  },
  cls_msg: {
    maxWidth: 256,
  },
}));

function SnackbarContentWrapper(props) {
  const classes = useSnackItemStyles();
  const { className, message, onClose, variant, ...other } = props;
  const Icon = variantIcon[variant];
  return (
    <SnackbarContent
      className={classnames(classes[variant], className)}
      classes={{ root: classes.root, message: classes.cls_msg }}
      message={
        <span className={classes.message}>
          {Icon && <Icon className={classnames(classes.icon, classes.iconVariant)} />}
          {message}
        </span>
      }
      action={[
        <IconButton key="close" onClick={onClose} className={classes.iconButton}>
          <BasicClose />
        </IconButton>,
      ]}
      {...other}
    />
  );
}

SnackbarContentWrapper.propTypes = {
  className: PropTypes.string,
  message: PropTypes.string,
  onClose: PropTypes.func,
  variant: PropTypes.oneOf(['error', 'info', 'success', 'warning', '']).isRequired,
};

SnackbarContentWrapper.defaultProps = {
  className: '',
  message: '',
  onClose: () => {},
};

const useStyles = makeStyles(() => ({
  snackBarWrapper: {
    position: 'fixed',
    top: 0,
    right: 0,
    zIndex: 99999,
  },

  snackBar: {
    position: 'relative',
    marginBottom: 24,
  },
}));

export default function Toast() {
  const classes = useStyles();
  const { toasts, hideToast, removeToast } = useToastContext();

  return (
    toasts.length > 0 && (
      <div className={classes.snackBarWrapper}>
        {toasts.map((item, index) => (
          <Snackbar
            classes={{ root: classes.snackBar }}
            key={item.message + index}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            autoHideDuration={3000}
            ClickAwayListenerProps={{ touchEvent: false, mouseEvent: false }}
            open={item.open}
            onClose={() => hideToast(item)}
            onExited={() => removeToast(item)}
          >
            <SnackbarContentWrapper
              onClose={() => hideToast(item)}
              variant={item ? item.icon : ''}
              message={item.message}
            />
          </Snackbar>
        ))}
      </div>
    )
  );
}
